import { useEffect, useState } from 'react';

import { Organization } from '@/types/organization';
import { User } from '@/types/userList';
import { useBulkUpdate } from './useBulkUpdate';
import { useCancelClick } from './useCancelClick';
import { useCancelPreview } from './useCancelPreview';
import { useDownloadCsv } from './useDownloadCsv';
import { useEditLinkClick } from './useEditLinkClick';
import { useFetchUserList } from './useFetchUserList';
import { useHasUpdateError } from './useHasUpdateError';
import { useRefreshPage } from './useRefreshPage';
import { useShowUserForm } from './useShowUserForm';
import { useUpdateUser } from './useUpdateUser';
import { useUploadCsv, UpdateError } from './useUploadCsv';

export const useUserList = (currentOrganization: Organization) => {
  const currentOrganizationId = currentOrganization.id;
  const gloplaId = currentOrganization.gloplaId;

  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [users, setUsers] = useState<User[] | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [csvFormatError, setCsvFormatError] = useState<string | null>(null);
  const [csvUpdateErrors, setCsvUpdateErrors] = useState<UpdateError[] | null>(
    null,
  );

  const { fetchUserList, isLoading } = useFetchUserList();

  const { refreshPage } = useRefreshPage({
    currentOrganizationId,
    fetchUserList,
    setCsvFormatError,
    setCsvUpdateErrors,
    setIsPreview,
    setSelectedUserId,
    setUsers,
  });

  const { updateUser } = useUpdateUser({
    organizationId: currentOrganizationId,
    selectedUserId,
    setIsUpdating,
    setSelectedUserId,
    setUsers,
  });

  const { bulkUpdate } = useBulkUpdate({
    organizationId: currentOrganizationId,
    refreshPage,
    setIsUpdating,
    users,
  });

  const { cancelPreview } = useCancelPreview(refreshPage);

  const { onDownloadCsvClick } = useDownloadCsv({
    organizationId: currentOrganizationId,
    gloplaId,
  });

  const { onUploadCsvClick } = useUploadCsv({
    currentOrganizationId,
    setCsvFormatError,
    setCsvUpdateErrors,
    setIsPreview,
    setIsUploading,
    setSelectedUserId,
    setUsers,
  });

  const { onEditLinkClick } = useEditLinkClick(setSelectedUserId);

  const { onCancelClick } = useCancelClick(setSelectedUserId);

  const { hasUpdateError } = useHasUpdateError(csvUpdateErrors);

  const { showUserForm } = useShowUserForm(isPreview, selectedUserId, users);

  useEffect(() => {
    fetchUserList(currentOrganizationId).then((fetchedUsers) =>
      setUsers(fetchedUsers),
    );
  }, [currentOrganizationId, fetchUserList]);

  return {
    bulkUpdate,
    cancelPreview,
    csvFormatError,
    csvUpdateErrors,
    hasUpdateError,
    isLoading,
    isPreview,
    isUpdating,
    isUploading,
    onCancelClick,
    onDownloadCsvClick,
    onEditLinkClick,
    onUploadCsvClick,
    selectedUserId,
    showUserForm,
    updateUser,
    users,
  };
};
